import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import CustomImage from "./CustomImage";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Strike from "@tiptap/extension-strike";
import CodeBlock from "@tiptap/extension-code-block";
import DOMPurify from "dompurify";

interface RichTextViewerProps {
    content: string;
}

const RichTextViewer: React.FC<RichTextViewerProps> = ({ content }) => {
    const editor = useEditor({
        editable: false,
        extensions: [
            StarterKit.configure({
                bulletList: { keepMarks: true, keepAttributes: false },
                orderedList: { keepMarks: true, keepAttributes: false },
            }),
            Strike,
            CodeBlock,
            Underline,
            TextStyle,
            Color,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: "text-[#3b82f6] underline cursor-pointer",
                },
            }),
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            CustomImage.configure({
                allowBase64: true,
                HTMLAttributes: {
                    class: "max-w-full h-auto rounded-lg",
                },
            }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            TaskItem.configure({ nested: true }),
            TaskList,
        ],
        // Additive allow-list: pins the Tiptap-generated attributes (task lists,
        // table spans) so a future dompurify minor under the ^3 range can't
        // silently strip them if its defaults tighten. Defaults still apply.
        content: DOMPurify.sanitize(content, {
            ADD_ATTR: ["data-type", "data-checked", "colspan", "rowspan"],
        }),
    });

    if (!editor) return null;

    return (
        <div className="announcement-markdown-content mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="prose prose-sm sm:prose lg:prose-lg xl:prose-2xl max-w-none marker:text-black">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
};

export default RichTextViewer;
